import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Save, Send, Clock, Mail, Server, Info, Building2, Upload, X, CalendarClock, Plus, Trash2, Banknote, ShieldCheck, FileDown } from "lucide-react";
import { toast } from "sonner";
import CompaniesSection from "@/components/CompaniesSection";
import { ConsultazioneUsersSection } from "@/components/ConsultazioneUsersSection";

/** Piccolo componente per gestire la lista di nomi custom (add/remove chip) */
function CollabCustomList({ names, onChange }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if ((names || []).some((n) => n.trim().toLowerCase() === v.toLowerCase())) {
      toast.error("Nome già presente");
      return;
    }
    onChange([...(names || []), v]);
    setDraft("");
  };
  const remove = (idx) => onChange((names || []).filter((_, i) => i !== idx));
  return (
    <div data-testid="collab-custom-list">
      <Label className="text-xs text-slate-600 uppercase tracking-wide">Nomi custom</Label>
      <div className="flex flex-wrap gap-1.5 mt-1.5 mb-2">
        {(names || []).length === 0 ? (
          <span className="text-xs text-slate-500 italic">Nessun nome custom. Aggiungine sotto.</span>
        ) : (
          names.map((n, i) => (
            <span
              key={`${n}-${i}`}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white border border-slate-300 text-xs"
              data-testid={`collab-custom-chip-${i}`}
            >
              {n}
              <button
                type="button"
                className="text-slate-400 hover:text-red-600"
                onClick={() => remove(i)}
                aria-label={`Rimuovi ${n}`}
                data-testid={`collab-custom-remove-${i}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))
        )}
      </div>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Es. Marco Rossi"
          className="max-w-xs"
          data-testid="collab-custom-input"
        />
        <Button
          type="button"
          size="sm"
          onClick={add}
          disabled={!draft.trim()}
          data-testid="collab-custom-add-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi
        </Button>
      </div>
    </div>
  );
}


const DAYS = [
  { key: "lun", label: "Lunedì" },
  { key: "mar", label: "Martedì" },
  { key: "mer", label: "Mercoledì" },
  { key: "gio", label: "Giovedì" },
  { key: "ven", label: "Venerdì" },
  { key: "sab", label: "Sabato" },
];

const SMTP_PRESETS = [
  { name: "Gmail",    host: "smtp.gmail.com",       port: 587, tls: true,  note: "Richiede una App Password (non la password normale)." },
  { name: "Outlook / Office 365", host: "smtp.office365.com", port: 587, tls: true, note: "Usa la tua email Microsoft." },
  { name: "Aruba",    host: "smtps.aruba.it",       port: 465, tls: false, note: "Porta 465 con SSL. Lascia TLS disattivato." },
  { name: "Libero",   host: "smtp.libero.it",       port: 465, tls: false, note: "Porta 465 con SSL." },
  { name: "Virgilio", host: "out.virgilio.it",      port: 465, tls: false, note: "Porta 465 con SSL." },
  { name: "Yahoo",    host: "smtp.mail.yahoo.com",  port: 587, tls: true,  note: "Richiede password specifica per app." },
];

export default function Impostazioni() {
  const { user, refreshMe } = useAuth();
  const isSoloPlan = user?.agency?.plan_id === "solo";
  const [s, setS] = useState(null);
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meVersMode, setMeVersMode] = useState("polizze");
  const [gdprLoading, setGdprLoading] = useState(false);
  const fileRef = useRef(null);

  const downloadGdprZip = async () => {
    setGdprLoading(true);
    try {
      const res = await api.get("/gdpr/export-zip", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      const safe = (agency?.nome_agenzia || "agenzia").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
      const today = new Date().toISOString().slice(0, 10);
      a.download = `gdpr_export_${safe}_${today}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Archivio GDPR scaricato");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setGdprLoading(false);
    }
  };

  const load = async () => {
    try {
      const [r, a] = await Promise.all([api.get("/settings"), api.get("/agency")]);
      const data = r.data;
      if (!data.closing_times) {
        data.closing_times = {
          lun: "18:30", mar: "18:30", mer: "18:30",
          gio: "18:30", ven: "18:30", sab: "13:00", dom: null,
        };
      }
      data.closing_times.dom = null;
      if (!Array.isArray(data.special_closings)) data.special_closings = [];
      setS(data);
      setAgency(a.data);
      setMeVersMode((user?.versamento_mode) || "polizze");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (user?.versamento_mode) setMeVersMode(user.versamento_mode);
  }, [user?.versamento_mode]);

  const set = (k, v) => setS((cur) => ({ ...cur, [k]: v }));
  const setDayTime = (dayKey, value) => {
    setS((cur) => ({ ...cur, closing_times: { ...(cur.closing_times || {}), [dayKey]: value } }));
  };
  const toggleDay = (dayKey, enabled) => {
    setS((cur) => ({
      ...cur,
      closing_times: { ...(cur.closing_times || {}), [dayKey]: enabled ? "18:30" : null },
    }));
  };

  const applyPreset = (preset) => {
    setS((cur) => ({
      ...cur,
      smtp_host: preset.host,
      smtp_port: preset.port,
      smtp_use_tls: preset.tls,
    }));
    toast.success(`Preset ${preset.name} applicato — completa username, password e mittente`);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Validate per-day times
      for (const d of DAYS) {
        const t = s.closing_times?.[d.key];
        if (t !== null && t !== "" && !/^([01]?\d|2[0-3]):[0-5]\d$/.test(t)) {
          toast.error(`Orario non valido per ${d.label}: ${t}`);
          setSaving(false);
          return;
        }
      }
      await Promise.all([
        api.put("/settings", s),
        api.put("/agency", {
          nome_agenzia: agency.nome_agenzia,
          partita_iva: agency.partita_iva,
          indirizzo: agency.indirizzo,
          codice_sdi: agency.codice_sdi,
          pec: agency.pec,
          logo_url: agency.logo_url || "",
          header_subtitle: agency.header_subtitle || "",
          consenti_spese: !!agency.consenti_spese,
          sospesi_abilitati: !!agency.sospesi_abilitati,
          sospesi_metodi: Array.isArray(agency.sospesi_metodi) ? agency.sospesi_metodi : [],
          recuperi_abilitati: !!agency.recuperi_abilitati,
          multi_polizza_enabled: !!agency.multi_polizza_enabled,
          controllo_cc_enabled: !!agency.controllo_cc_enabled,
          raggruppa_compagnie: agency.raggruppa_compagnie === undefined ? true : !!agency.raggruppa_compagnie,
          use_platform_smtp: agency.use_platform_smtp === undefined ? true : !!agency.use_platform_smtp,
          sections_below_entries: !!agency.sections_below_entries,
          pdf_orientation: agency.pdf_orientation || "portrait",
          pos_split_mode: agency.pos_split_mode || "none",
          pos_methods: Array.isArray(agency.pos_methods) ? agency.pos_methods : ["Bancomat"],
          fc_agenzia_collab_col_enabled: !!agency.fc_agenzia_collab_col_enabled,
          fc_agenzia_collab_source: agency.fc_agenzia_collab_source || "utenze",
          fc_agenzia_collab_custom_names: Array.isArray(agency.fc_agenzia_collab_custom_names) ? agency.fc_agenzia_collab_custom_names : [],
          fc_show_data_ora_incasso: agency.fc_show_data_ora_incasso === undefined ? true : !!agency.fc_show_data_ora_incasso,
          fc_show_collab_name: agency.fc_show_collab_name === undefined ? true : !!agency.fc_show_collab_name,
        }),
        // Modalità versamenti personale dell'AGENTE (si applica solo a lui)
        api.put("/me/versamento-mode", { versamento_mode: meVersMode }),
      ]);
      toast.success("Impostazioni salvate");
      // notify other components that branding changed
      window.dispatchEvent(new Event("agency:updated"));
      refreshMe?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const onPickLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Seleziona un file immagine");
      return;
    }
    if (file.size > 500_000) {
      toast.error("L'immagine è troppo grande (max 500KB). Comprimi il logo prima di caricarlo.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setAgency((a) => ({ ...a, logo_url: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setAgency((a) => ({ ...a, logo_url: "" }));
    if (fileRef.current) fileRef.current.value = "";
  };

  const setAg = (k, v) => setAgency((a) => ({ ...a, [k]: v }));

  // ---- Special (one-off) closings ----
  const [newSpecial, setNewSpecial] = useState({ date: "", time: "18:30", note: "" });

  const todayIso = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const fmtItalianDate = (iso) => {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  };

  const addSpecialClosing = () => {
    const { date, time, note } = newSpecial;
    if (!date || !time) {
      toast.error("Data e orario sono obbligatori");
      return;
    }
    if (!/^([01]?\d|2[0-3]):[0-5]\d$/.test(time)) {
      toast.error(`Orario non valido: ${time}`);
      return;
    }
    if (date < todayIso()) {
      toast.error("La data deve essere oggi o futura");
      return;
    }
    const list = s.special_closings || [];
    if (list.some((x) => x.date === date)) {
      toast.error("Esiste già una chiusura speciale per questa data");
      return;
    }
    const next = [...list, { date, time, note: note || "" }]
      .sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time));
    setS((cur) => ({ ...cur, special_closings: next }));
    setNewSpecial({ date: "", time: "18:30", note: "" });
    toast.success(`Chiusura speciale aggiunta per ${fmtItalianDate(date)}`);
  };

  const removeSpecialClosing = (date) => {
    setS((cur) => ({
      ...cur,
      special_closings: (cur.special_closings || []).filter((x) => x.date !== date),
    }));
  };

  const testEmail = async () => {
    try {
      const r = await api.post("/settings/test-email");
      if (r.data.stato_invio === "inviata") toast.success("Email di test inviata correttamente");
      else if (r.data.stato_invio === "non_configurato") toast.error("SMTP non configurato. Compila i dati SMTP qui sotto.");
      else toast.error(`Errore invio: ${r.data.error || "sconosciuto"}`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading || !s || !agency) return <div className="text-slate-500 text-sm">Caricamento…</div>;

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Configurazione</div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">Impostazioni</h1>
        <p className="text-sm text-slate-500 mt-1">Intestazione, orari di chiusura, email e configurazione SMTP.</p>
      </div>

      {/* Intestazione / Branding */}
      <Card className="p-6 border-slate-200">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center"><Building2 className="w-4 h-4 text-slate-700" /></div>
          <h2 className="font-heading text-lg font-semibold">Intestazione e branding</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5">
          <div className="md:col-span-1">
            <Label>Logo agenzia</Label>
            <div className="mt-2 border-2 border-dashed border-slate-200 rounded-lg p-4 text-center">
              {agency.logo_url ? (
                <div className="space-y-3">
                  <img src={agency.logo_url} alt="Logo" className="max-h-24 mx-auto" data-testid="logo-preview" />
                  <div className="flex gap-2 justify-center">
                    <Button type="button" size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="change-logo">
                      <Upload className="w-3.5 h-3.5 mr-1" /> Cambia
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={removeLogo} className="text-red-600" data-testid="remove-logo">
                      <X className="w-3.5 h-3.5 mr-1" /> Rimuovi
                    </Button>
                  </div>
                </div>
              ) : (
                <button type="button" onClick={() => fileRef.current?.click()} className="text-sm text-slate-500 hover:text-slate-900" data-testid="upload-logo">
                  <Upload className="w-6 h-6 mx-auto mb-2" />
                  Carica logo
                  <div className="text-xs text-slate-400 mt-1">PNG, JPG, SVG · max 500KB</div>
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickLogo} />
            </div>
          </div>
          <div className="md:col-span-2 space-y-3">
            <div>
              <Label>Nome agenzia</Label>
              <Input data-testid="agency-nome" value={agency.nome_agenzia || ""} onChange={(e) => setAg("nome_agenzia", e.target.value)} />
              <div className="text-xs text-slate-500 mt-1">Compare nell'intestazione della tua area, sui PDF e nelle email.</div>
            </div>
            <div>
              <Label>Sottotitolo (opzionale)</Label>
              <Input data-testid="agency-subtitle" placeholder="Es. Agenzia Generale di Milano Centro" value={agency.header_subtitle || ""} onChange={(e) => setAg("header_subtitle", e.target.value)} />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Partita IVA</Label>
                <Input data-testid="agency-piva" value={agency.partita_iva || ""} onChange={(e) => setAg("partita_iva", e.target.value)} />
              </div>
              <div>
                <Label>Codice SDI</Label>
                <Input data-testid="agency-sdi" value={agency.codice_sdi || ""} onChange={(e) => setAg("codice_sdi", e.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <Label>Indirizzo</Label>
                <Input data-testid="agency-indirizzo" value={agency.indirizzo || ""} onChange={(e) => setAg("indirizzo", e.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <Label>PEC</Label>
                <Input type="email" data-testid="agency-pec" value={agency.pec || ""} onChange={(e) => setAg("pec", e.target.value)} />
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Orari di chiusura per giorno */}
      <Card className="p-6 border-slate-200">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center"><Clock className="w-4 h-4 text-slate-700" /></div>
          <h2 className="font-heading text-lg font-semibold">Orari di chiusura settimanali</h2>
        </div>
        <div className="text-xs text-slate-500 mb-4 inline-flex items-center gap-1.5">
          <Info className="w-3.5 h-3.5" />
          Fuso orario: Europe/Rome. Disattivando un giorno, gli incassi inseriti in quel giorno confluiscono nel foglio del successivo giorno lavorativo.
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {DAYS.map((d) => {
            const enabled = !!s.closing_times?.[d.key];
            return (
              <div key={d.key} className="flex items-center gap-3 border border-slate-200 rounded-lg p-3 bg-white">
                <Switch
                  data-testid={`closing-toggle-${d.key}`}
                  checked={enabled}
                  onCheckedChange={(v) => toggleDay(d.key, v)}
                />
                <div className="flex-1">
                  <div className="font-medium text-slate-900 text-sm">{d.label}</div>
                  {!enabled && <div className="text-xs text-slate-500">Nessuna chiusura</div>}
                </div>
                <Input
                  data-testid={`closing-time-${d.key}`}
                  type="time"
                  disabled={!enabled}
                  value={s.closing_times?.[d.key] || ""}
                  onChange={(e) => setDayTime(d.key, e.target.value)}
                  className="w-32"
                />
              </div>
            );
          })}
          {/* Sunday locked */}
          <div className="flex items-center gap-3 border border-slate-200 rounded-lg p-3 bg-slate-50">
            <Switch checked={false} disabled data-testid="closing-toggle-dom" />
            <div className="flex-1">
              <div className="font-medium text-slate-700 text-sm">Domenica</div>
              <div className="text-xs text-slate-500">Nessuna chiusura — gli incassi vanno al lunedì</div>
            </div>
            <Input type="time" disabled value="" className="w-32 bg-slate-100" />
          </div>
        </div>

        {!isSoloPlan && (
        <div className="mt-4 border-t border-slate-100 pt-4 flex items-start justify-between gap-4">
          <div className="flex-1">
            <Label>Applica questi orari anche al foglio cassa di agenzia</Label>
            <div className="text-xs text-slate-500 mt-1">
              {s.agency_sheet_follows_closing_times === false
                ? "Il foglio cassa di agenzia NON segue gli orari di chiusura settimanali: viene chiuso manualmente o automaticamente alle 23:59."
                : "Il foglio cassa di agenzia segue gli stessi orari di chiusura dei collaboratori. Disattiva se vuoi chiuderlo manualmente o lasciarlo aperto fino a mezzanotte."}
            </div>
          </div>
          <Switch
            data-testid="setting-agency-follows-closing"
            checked={s.agency_sheet_follows_closing_times !== false}
            onCheckedChange={(v) => set("agency_sheet_follows_closing_times", v)}
          />
        </div>
        )}

        <div className="mt-4">
          <Label>Formato allegato riepilogo</Label>
          <Select value={s.attachment_format || "both"} onValueChange={(v) => set("attachment_format", v)}>
            <SelectTrigger className="md:w-72" data-testid="setting-attachment-format"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pdf">Solo PDF</SelectItem>
              <SelectItem value="excel">Solo Excel</SelectItem>
              <SelectItem value="both">PDF + Excel</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="mt-4">
          <Label>Ordinamento incassi a chiusura foglio cassa</Label>
          <Select value={s.sort_entries_on_close || "inserimento"} onValueChange={(v) => set("sort_entries_on_close", v)}>
            <SelectTrigger className="md:w-72" data-testid="setting-sort-entries"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="inserimento">Ordine di inserimento (default)</SelectItem>
              <SelectItem value="contraente">Ordine alfabetico contraente</SelectItem>
              <SelectItem value="importo">Importo decrescente</SelectItem>
              <SelectItem value="metodo">Metodo di pagamento</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-xs text-slate-500 mt-1">
            Determina come saranno ordinate le righe nei riepiloghi PDF / Excel inviati a chiusura. Gli incassi urgenti rimangono comunque in cima all&apos;elenco.
          </div>
        </div>
      </Card>

      {/* Chiusure speciali (one-off) */}
      <Card className="p-6 border-slate-200">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center"><CalendarClock className="w-4 h-4 text-slate-700" /></div>
          <h2 className="font-heading text-lg font-semibold">Chiusure speciali (una tantum)</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4 inline-flex items-center gap-1.5">
          <Info className="w-3.5 h-3.5" />
          Imposta un orario di chiusura diverso per un singolo giorno. Sovrascrive l'orario settimanale solo per la data scelta e si esegue una sola volta.
        </p>

        <div className="grid sm:grid-cols-12 gap-3 items-end mb-4">
          <div className="sm:col-span-3">
            <Label>Data</Label>
            <Input
              type="date"
              min={todayIso()}
              data-testid="special-closing-date"
              value={newSpecial.date}
              onChange={(e) => setNewSpecial((n) => ({ ...n, date: e.target.value }))}
            />
          </div>
          <div className="sm:col-span-2">
            <Label>Orario</Label>
            <Input
              type="time"
              data-testid="special-closing-time"
              value={newSpecial.time}
              onChange={(e) => setNewSpecial((n) => ({ ...n, time: e.target.value }))}
            />
          </div>
          <div className="sm:col-span-5">
            <Label>Nota (opzionale)</Label>
            <Input
              data-testid="special-closing-note"
              placeholder="Es. chiusura anticipata festività"
              value={newSpecial.note}
              onChange={(e) => setNewSpecial((n) => ({ ...n, note: e.target.value }))}
            />
          </div>
          <div className="sm:col-span-2">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={addSpecialClosing}
              data-testid="add-special-closing"
            >
              <Plus className="w-4 h-4 mr-1" /> Aggiungi
            </Button>
          </div>
        </div>

        {(s.special_closings || []).length === 0 ? (
          <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg p-4 text-center" data-testid="special-closings-empty">
            Nessuna chiusura speciale impostata
          </div>
        ) : (
          <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg overflow-hidden" data-testid="special-closings-list">
            {(s.special_closings || []).map((sc) => (
              <div key={sc.date} className="flex items-center gap-3 p-3 bg-white" data-testid={`special-closing-row-${sc.date}`}>
                <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center">
                  <CalendarClock className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900">
                    {fmtItalianDate(sc.date)} — ore {sc.time}
                  </div>
                  {sc.note && <div className="text-xs text-slate-500 truncate">{sc.note}</div>}
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => removeSpecialClosing(sc.date)}
                  data-testid={`remove-special-closing-${sc.date}`}
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Rimuovi
                </Button>
              </div>
            ))}
          </div>
        )}
        <div className="text-xs text-slate-400 mt-3">
          Ricordati di salvare le impostazioni per applicare le modifiche.
        </div>
      </Card>

      {/* Cassa — solo agente con add-on flusso cassa */}
      <Card className="p-6 border-slate-200">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center"><Banknote className="w-4 h-4 text-slate-700" /></div>
          <h2 className="font-heading text-lg font-semibold">Cassa</h2>
        </div>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <Label className="text-slate-700">Consenti registrazione spese</Label>
            <div className="text-xs text-slate-500 mt-1">
              {agency.flusso_cassa_agente
                ? "Sul foglio cassa di agenzia compare il pulsante \"Aggiungi uscita\" per registrare spese, pagamenti di fatture e altre uscite di liquidità. Le uscite vengono automaticamente sottratte dal saldo del flusso di cassa."
                : <span className="text-amber-700">Disponibile solo con il modulo <strong>Flusso di cassa agente</strong>. Attivalo da Abbonamento.</span>}
            </div>
          </div>
          <Switch
            data-testid="agency-consenti-spese"
            checked={!!agency.consenti_spese}
            onCheckedChange={(v) => setAg("consenti_spese", v)}
            disabled={!agency.flusso_cassa_agente}
          />
        </div>

        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <Label className="text-slate-700">Sospesi agenzia</Label>
              <div className="text-xs text-slate-500 mt-1">
                Abilita i metodi di pagamento <strong>&quot;Sospeso&quot;</strong> sul foglio cassa (agente + collaboratori con sospesi abilitati). Scegli quali sotto-metodi ammettere: solo quelli attivati appariranno nel dropdown metodo. Automaticamente per ogni sospeso attivo esisterà anche il rispettivo <strong>Recupero</strong> corrispondente.
              </div>
            </div>
            <Switch
              data-testid="agency-sospesi"
              checked={!!agency.sospesi_abilitati}
              onCheckedChange={(v) => setAg("sospesi_abilitati", v)}
            />
          </div>
          {agency.sospesi_abilitati ? (
            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <Label className="text-xs text-slate-600 uppercase tracking-wide">Metodi di sospeso ammessi</Label>
              <div className="flex flex-wrap gap-3 mt-2" data-testid="sospesi-metodi-picker">
                {[
                  { k: "contante", lbl: "Sospeso contante" },
                  { k: "assegno",  lbl: "Sospeso assegno" },
                  { k: "bonifico", lbl: "Sospeso bonifico" },
                  { k: "pos",      lbl: "Sospeso POS" },
                  { k: "postale",  lbl: "Sospeso postale" },
                ].map((opt) => {
                  const active = (agency.sospesi_metodi || []).includes(opt.k);
                  return (
                    <label
                      key={opt.k}
                      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm cursor-pointer select-none ${
                        active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"
                      }`}
                      data-testid={`sospeso-metodo-${opt.k}`}
                    >
                      <input
                        type="checkbox"
                        className="hidden"
                        checked={active}
                        onChange={(e) => {
                          const current = Array.isArray(agency.sospesi_metodi) ? [...agency.sospesi_metodi] : [];
                          const next = e.target.checked
                            ? Array.from(new Set([...current, opt.k]))
                            : current.filter((k) => k !== opt.k);
                          setAg("sospesi_metodi", next);
                        }}
                      />
                      {opt.lbl}
                    </label>
                  );
                })}
              </div>
              {(agency.sospesi_metodi || []).length === 0 ? (
                <div className="text-xs text-amber-700 mt-2">
                  Nessun sotto-metodo selezionato: verrà mostrato il metodo generico <strong>&quot;Sospeso&quot;</strong> come nel comportamento precedente.
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="flex items-start justify-between gap-4 mt-4 pt-4 border-t border-slate-100">
          <div className="flex-1">
            <Label className="text-slate-700">Recuperi agenzia</Label>
            <div className="text-xs text-slate-500 mt-1">
              Abilita i metodi di pagamento <strong>"Recupero contante"</strong> e <strong>"Recupero assegno"</strong> sul foglio cassa di agenzia. Usali per registrare incassi recuperati da polizze già caricate in precedenza. Visibili solo sul foglio cassa dell'agente.
            </div>
          </div>
          <Switch
            data-testid="agency-recuperi"
            checked={!!agency.recuperi_abilitati}
            onCheckedChange={(v) => setAg("recuperi_abilitati", v)}
          />
        </div>

        <div className="flex items-start justify-between gap-4 mt-4 pt-4 border-t border-slate-100">
          <div className="flex-1">
            <Label className="text-slate-700">Incasso multiplo</Label>
            <div className="text-xs text-slate-500 mt-1">
              Abilita l&apos;inserimento di <strong>più polizze in un&apos;unica transazione</strong> (es. un solo pagamento Bancomat che copre 2 polizze diverse). Sui fogli cassa comparirà il pulsante <strong>&quot;Incasso multiplo&quot;</strong>. La funzione è disponibile per <strong>tutte le utenze dell&apos;agenzia</strong> (agente, collaboratori, utenze interne).
            </div>
          </div>
          <Switch
            data-testid="agency-multi-polizza"
            checked={!!agency.multi_polizza_enabled}
            onCheckedChange={(v) => setAg("multi_polizza_enabled", v)}
          />
        </div>

        <div className="flex items-start justify-between gap-4 mt-4 pt-4 border-t border-slate-100">
          <div className="flex-1">
            <Label className="text-slate-700">Controllo C/C (riconciliazione bonifici)</Label>
            <div className="text-xs text-slate-500 mt-1">
              Attiva una nuova sezione <strong>&quot;Controllo C/C&quot;</strong> nel menu dell&apos;agente. Vi troverai tutti i bonifici incassati dall&apos;agenzia e dai collaboratori senza conto separato, con la possibilità di spuntarli come effettivamente accreditati sul conto corrente. I bonifici già confermati da un versamento convalidato vengono auto-marcati come riconciliati.
            </div>
          </div>
          <Switch
            data-testid="agency-controllo-cc"
            checked={!!agency.controllo_cc_enabled}
            onCheckedChange={(v) => setAg("controllo_cc_enabled", v)}
          />
        </div>

        {/* NEW: Colonna "Collaboratore associato" sul FC agenzia */}
        <div className="mt-4 pt-4 border-t border-slate-100" hidden={isSoloPlan}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <Label className="text-slate-700">Colonna &quot;Collaboratore associato&quot; sul Foglio Cassa Agenzia</Label>
              <div className="text-xs text-slate-500 mt-1">
                Aggiunge una colonna extra al <strong>foglio cassa agenzia</strong> (compilato dall&apos;agente/utenza interna) per associare un collaboratore a ciascun titolo incassato. Utile per tracciare i titoli incassati dall&apos;agenzia per conto di un collaboratore: se il titolo va in <strong>Sospeso</strong>, apparirà nella lista dei sospesi filtrabile per collaboratore.
              </div>
            </div>
            <Switch
              data-testid="agency-collab-col-enabled"
              checked={!!agency.fc_agenzia_collab_col_enabled}
              onCheckedChange={(v) => setAg("fc_agenzia_collab_col_enabled", v)}
            />
          </div>
          {agency.fc_agenzia_collab_col_enabled ? (
            <div className="mt-4 space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div>
                <Label className="text-xs text-slate-600 uppercase tracking-wide">Sorgente nomi</Label>
                <div className="flex flex-wrap gap-2 mt-1.5" data-testid="collab-source-toggle">
                  {[
                    { v: "utenze", label: "Utenze (collaboratori registrati)" },
                    { v: "custom", label: "Custom (elenco manuale)" },
                    { v: "mista", label: "Mista (utenze + custom)" },
                  ].map((opt) => (
                    <Button
                      key={opt.v}
                      type="button"
                      variant={agency.fc_agenzia_collab_source === opt.v ? "default" : "outline"}
                      size="sm"
                      className={agency.fc_agenzia_collab_source === opt.v ? "bg-slate-900 text-white" : ""}
                      onClick={() => setAg("fc_agenzia_collab_source", opt.v)}
                      data-testid={`collab-source-${opt.v}`}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
              </div>
              {(agency.fc_agenzia_collab_source === "custom" || agency.fc_agenzia_collab_source === "mista") ? (
                <CollabCustomList
                  names={agency.fc_agenzia_collab_custom_names || []}
                  onChange={(next) => setAg("fc_agenzia_collab_custom_names", next)}
                />
              ) : null}
            </div>
          ) : null}
        </div>

        {/* NEW: Toggle visualizzazione sul foglio di chiusura */}
        <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <Label className="text-slate-700">Mostra data e ora di incasso sul foglio di chiusura</Label>
              <div className="text-xs text-slate-500 mt-1">
                Se disattivato, la colonna <strong>&quot;Inserito il&quot;</strong> non comparirà nei PDF/Excel di chiusura di tutti i fogli cassa (agenzia + collaboratori).
              </div>
            </div>
            <Switch
              data-testid="agency-show-data-ora"
              checked={agency.fc_show_data_ora_incasso === undefined ? true : !!agency.fc_show_data_ora_incasso}
              onCheckedChange={(v) => setAg("fc_show_data_ora_incasso", v)}
            />
          </div>
          <div className="flex items-start justify-between gap-4 pt-3 border-t border-slate-100" hidden={isSoloPlan} style={isSoloPlan ? { display: "none" } : undefined}>
            <div className="flex-1">
              <Label className="text-slate-700">Mostra nome collaboratore associato sul foglio di chiusura</Label>
              <div className="text-xs text-slate-500 mt-1">
                Se attivo, sul PDF/Excel del foglio agenzia comparirà una colonna <strong>&quot;Collab.&quot;</strong> con il nome abbreviato (es. <em>M. Rossi</em>; se il cognome supera 5 lettere viene troncato con &laquo;…&raquo;). Se non c&apos;è nessun collaboratore associato la colonna resta nascosta automaticamente.
              </div>
            </div>
            <Switch
              data-testid="agency-show-collab-name"
              checked={agency.fc_show_collab_name === undefined ? true : !!agency.fc_show_collab_name}
              onCheckedChange={(v) => setAg("fc_show_collab_name", v)}
            />
          </div>
        </div>


        <div className="flex items-start justify-between gap-4 mt-4 pt-4 border-t border-slate-100">
          <div className="flex-1">
            <Label className="text-slate-700">Sposta "Cassa di agenzia" e "Spese e pagamenti" sotto la lista incassi</Label>
            <div className="text-xs text-slate-500 mt-1">
              Per impostazione predefinita i blocchi <strong>Cassa di agenzia</strong> (flusso di cassa) e <strong>Spese e pagamenti</strong> sono mostrati sopra la lista degli incassi. Attiva questa opzione per averli <strong>sotto</strong> la tabella, così metti in primo piano l'inserimento delle polizze.
            </div>
          </div>
          <Switch
            data-testid="agency-sections-below"
            checked={!!agency.sections_below_entries}
            onCheckedChange={(v) => setAg("sections_below_entries", v)}
          />
        </div>

        <div className="mt-4 pt-4 border-t border-slate-100">
          <Label className="text-slate-700">Orientamento PDF dei fogli cassa</Label>
          <div className="text-xs text-slate-500 mt-1 mb-3">
            Scegli come stampare/inviare i fogli cassa in PDF. <strong>Verticale</strong> è più compatto, <strong>Orizzontale</strong> mostra più colonne (utile con plurimandatari, abbuoni e tante voci).
          </div>
          <div className="flex flex-wrap gap-2" data-testid="pdf-orientation-toggle">
            <Button
              type="button"
              variant={agency.pdf_orientation !== "landscape" ? "default" : "outline"}
              size="sm"
              onClick={() => setAg("pdf_orientation", "portrait")}
              data-testid="pdf-orientation-portrait"
            >
              📄 Verticale (A4)
            </Button>
            <Button
              type="button"
              variant={agency.pdf_orientation === "landscape" ? "default" : "outline"}
              size="sm"
              onClick={() => setAg("pdf_orientation", "landscape")}
              data-testid="pdf-orientation-landscape"
            >
              📃 Orizzontale (A4 landscape)
            </Button>
          </div>
        </div>

        {/* POS split mode */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          <Label className="text-slate-700">Foglio cassa POS separato in chiusura</Label>
          <div className="text-xs text-slate-500 mt-1 mb-3">
            Alla chiusura del foglio cassa, l'app può separare i movimenti POS in un file dedicato (utile per la contabilità). Tutti i file vengono inviati nella stessa email.
          </div>
          <div className="flex flex-wrap gap-2 mb-3" data-testid="pos-split-toggle">
            <Button
              type="button"
              size="sm"
              variant={(agency.pos_split_mode || "none") === "none" ? "default" : "outline"}
              onClick={() => setAg("pos_split_mode", "none")}
              data-testid="pos-split-none"
            >
              Tutto su un foglio
            </Button>
            <Button
              type="button"
              size="sm"
              variant={agency.pos_split_mode === "separato" ? "default" : "outline"}
              onClick={() => setAg("pos_split_mode", "separato")}
              data-testid="pos-split-separato"
            >
              POS in foglio separato
            </Button>
            <Button
              type="button"
              size="sm"
              variant={agency.pos_split_mode === "duplicato" ? "default" : "outline"}
              onClick={() => setAg("pos_split_mode", "duplicato")}
              data-testid="pos-split-duplicato"
            >
              POS separato + anche sul principale
            </Button>
          </div>
          {agency.pos_split_mode && agency.pos_split_mode !== "none" && (
            <div className="bg-slate-50 rounded-lg p-3">
              <Label className="text-xs text-slate-600">Metodi considerati POS</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {["Contante", "Assegno", "Bancomat", "Bonifico", "Bollettino postale"].map((m) => {
                  const checked = (agency.pos_methods || ["Bancomat"]).includes(m);
                  return (
                    <Button
                      key={m}
                      type="button"
                      size="sm"
                      variant={checked ? "default" : "outline"}
                      onClick={() => {
                        const current = agency.pos_methods || ["Bancomat"];
                        const next = checked ? current.filter((x) => x !== m) : [...current, m];
                        setAg("pos_methods", next);
                      }}
                      data-testid={`pos-method-${m.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      {m}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Versamento mode — PERSONALE dell'agente */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          <Label className="text-slate-700">Tua modalità versamenti in banca</Label>
          <div className="text-xs text-slate-500 mt-1 mb-3">
            <strong>Per polizze</strong>: quando fai un versamento spunti le singole polizze. <strong>Importo libero</strong>: inserisci solo l&apos;importo totale, senza dettaglio polizze. <strong>Questa scelta si applica solo a te</strong>: ogni collaboratore ha la propria impostazione nella sua scheda in <em>Utenze</em>.
          </div>
          <div className="flex flex-wrap gap-2" data-testid="versamento-mode-toggle">
            <Button
              type="button"
              size="sm"
              variant={meVersMode === "polizze" ? "default" : "outline"}
              onClick={() => setMeVersMode("polizze")}
              data-testid="versamento-mode-polizze"
            >
              Per polizze (con spunte)
            </Button>
            <Button
              type="button"
              size="sm"
              variant={meVersMode === "importo_libero" ? "default" : "outline"}
              onClick={() => setMeVersMode("importo_libero")}
              data-testid="versamento-mode-libero"
            >
              Importo libero (manuale)
            </Button>
          </div>
        </div>
      </Card>

      {/* Compagnie (Plurimandatari) */}
      <CompaniesSection agency={agency} onToggleRaggruppa={(v) => setAg("raggruppa_compagnie", v)} />

      {/* Email destinatari */}
      <Card className="p-6 border-slate-200">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center"><Mail className="w-4 h-4 text-slate-700" /></div>
          <h2 className="font-heading text-lg font-semibold">Destinatari email</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Email notifiche urgenti</Label>
            <Input type="email" data-testid="setting-urgent-email" value={s.urgent_notification_email || ""} onChange={(e) => set("urgent_notification_email", e.target.value)} placeholder="agente@agenzia.it" />
          </div>
          <div>
            <Label>Email riepilogo giornaliero</Label>
            <Input type="email" data-testid="setting-daily-email" value={s.daily_cash_sheet_email || ""} onChange={(e) => set("daily_cash_sheet_email", e.target.value)} placeholder="agente@agenzia.it" />
          </div>
          <div>
            <Label>Email in copia (CC)</Label>
            <Input type="email" data-testid="setting-cc-email" value={s.cc_email || ""} onChange={(e) => set("cc_email", e.target.value)} placeholder="opzionale" />
          </div>
          <div className="flex items-end justify-between gap-3">
            <div>
              <Label>Invia copia al collaboratore</Label>
              <div className="text-xs text-slate-500">Il collaboratore riceve il proprio foglio cassa via email.</div>
            </div>
            <Switch data-testid="setting-send-copy" checked={!!s.send_copy_to_collaborator} onCheckedChange={(v) => set("send_copy_to_collaborator", v)} />
          </div>
        </div>
      </Card>

      {/* SMTP */}
      <Card className="p-6 border-slate-200">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center"><Server className="w-4 h-4 text-slate-700" /></div>
          <h2 className="font-heading text-lg font-semibold">Server SMTP (invio email)</h2>
        </div>

        {/* Toggle: usa SMTP piattaforma */}
        <div className="border border-blue-200 bg-blue-50/50 rounded-lg p-4 mb-5 flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="font-semibold text-slate-900 flex items-center gap-2">
              <Mail className="w-4 h-4 text-blue-700" />
              Usa il server email della piattaforma (Incassi Cloud)
            </div>
            <p className="text-xs text-slate-600 mt-1 leading-relaxed">
              Quando attivo, tutte le email (fogli cassa giornalieri, versamenti, urgenti, distinte) vengono inviate dalla casella ufficiale <strong>info@incassi.cloud</strong> senza nessuna configurazione da parte tua.<br/>
              Disattiva questo toggle solo se vuoi inviare le email dal <strong>tuo dominio aziendale</strong> (es. agenzia@tuodominio.it) — in quel caso compila i campi qui sotto.
            </p>
          </div>
          <Switch
            data-testid="setting-use-platform-smtp"
            checked={agency?.use_platform_smtp !== false}
            onCheckedChange={(v) => setAgency((cur) => ({ ...cur, use_platform_smtp: v }))}
          />
        </div>

        <p className={`text-xs text-slate-500 mb-4 ${agency?.use_platform_smtp !== false ? "opacity-60" : ""}`}>
          Inserisci i dati SMTP del tuo provider email. Funziona con <strong>qualsiasi servizio</strong> (Gmail, Outlook, Aruba, Libero, Yahoo, server aziendale…). Seleziona un preset qui sotto per pre-compilare host e porta.
        </p>

        <div className={`flex flex-wrap gap-2 mb-5 ${agency?.use_platform_smtp !== false ? "opacity-60 pointer-events-none" : ""}`}>
          {SMTP_PRESETS.map((p) => (
            <Button key={p.name} size="sm" variant="outline" onClick={() => applyPreset(p)} data-testid={`preset-${p.name.toLowerCase().replace(/[^a-z0-9]/g, "")}`}>
              {p.name}
            </Button>
          ))}
        </div>

        <fieldset disabled={agency?.use_platform_smtp !== false} className={agency?.use_platform_smtp !== false ? "opacity-60" : ""}>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <Label>SMTP Host</Label>
            <Input data-testid="setting-smtp-host" value={s.smtp_host || ""} onChange={(e) => set("smtp_host", e.target.value)} placeholder="smtp.gmail.com / smtps.aruba.it / smtp.libero.it" />
          </div>
          <div>
            <Label>Porta</Label>
            <Input type="number" data-testid="setting-smtp-port" value={s.smtp_port || 587} onChange={(e) => set("smtp_port", parseInt(e.target.value || "0", 10))} />
            <div className="text-xs text-slate-500 mt-1">587 = STARTTLS · 465 = SSL</div>
          </div>
          <div>
            <Label>Username (di solito = email completa)</Label>
            <Input data-testid="setting-smtp-user" value={s.smtp_username || ""} onChange={(e) => set("smtp_username", e.target.value)} placeholder="noreply@agenzia.it" />
          </div>
          <div>
            <Label>Password</Label>
            <Input type="password" data-testid="setting-smtp-pwd" value={s.smtp_password || ""} onChange={(e) => set("smtp_password", e.target.value)} placeholder="••••••••" />
            <div className="text-xs text-slate-500 mt-1">Per Gmail/Yahoo serve una App Password.</div>
          </div>
          <div>
            <Label>Mittente (From)</Label>
            <Input type="email" data-testid="setting-smtp-from" value={s.smtp_from || ""} onChange={(e) => set("smtp_from", e.target.value)} placeholder="noreply@agenzia.it" />
          </div>
          <div>
            <Label>Nome mittente</Label>
            <Input data-testid="setting-smtp-from-name" value={s.smtp_from_name || ""} onChange={(e) => set("smtp_from_name", e.target.value)} />
          </div>
          <div className="flex items-end justify-between gap-3">
            <div>
              <Label>Usa TLS (STARTTLS)</Label>
              <div className="text-xs text-slate-500">Per porta 465 (SSL) lasciare disattivato.</div>
            </div>
            <Switch data-testid="setting-smtp-tls" checked={!!s.smtp_use_tls} onCheckedChange={(v) => set("smtp_use_tls", v)} />
          </div>
        </div>
        </fieldset>
      </Card>

      <ConsultazioneUsersSection isUnlimited={agency?.plan_id === "unlimited" || (agency?.plan_id === "custom" && agency?.custom_plan?.max_consultazione !== 0)} />

      {/* Zona dati e portabilità (GDPR art. 20) */}
      <Card className="p-6 border-slate-200" data-testid="gdpr-section">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
            <ShieldCheck className="w-4 h-4 text-slate-700" />
          </div>
          <div>
            <h2 className="font-heading text-lg font-semibold">I tuoi dati (GDPR)</h2>
            <div className="text-xs text-slate-500">Diritto alla portabilità — Regolamento (UE) 2016/679, art. 20.</div>
          </div>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          Scarica in un unico archivio ZIP tutti i dati della tua agenzia
          (anagrafica, utenti, fogli cassa, incassi, versamenti, spese, compagnie,
          consensi, log email). Formato JSON (leggibile e machine-readable) più
          un CSV degli incassi apribile in Excel.
        </p>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={downloadGdprZip}
            disabled={gdprLoading}
            data-testid="gdpr-download-btn"
          >
            <FileDown className={`w-4 h-4 mr-2 ${gdprLoading ? "animate-pulse" : ""}`} />
            {gdprLoading ? "Preparazione archivio…" : "Scarica archivio dati (ZIP)"}
          </Button>
          <div className="text-xs text-slate-500">
            Limite: 3 export/ora. L&apos;archivio contiene dati personali dei tuoi
            collaboratori — conservalo cifrato.
          </div>
        </div>
      </Card>

      <div className="flex justify-end gap-3 sticky bottom-4">
        <Button variant="outline" onClick={testEmail} data-testid="test-email-btn"><Send className="w-4 h-4 mr-2" /> Invia email di test</Button>
        <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="save-settings-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Salvataggio…" : "Salva impostazioni"}
        </Button>
      </div>
    </div>
  );
}
